import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Injectable,
  input,
  OnInit,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter } from '@angular/material/core';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
  MatDateRangeSelectionStrategy,
} from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import moment from 'moment';
import type { Moment } from 'moment';
import { ChartRangeOptions } from '@core/models';
import { TelemetryPeriod, telemetryPeriod } from '@core/constants/telemetry-route.constant';

const DATE_FORMATS = {
  parse: { dateInput: 'YYYY-MM-DD' },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
class WeekRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter<D>);

  selectionFinished(date: D | null): DateRange<D> {
    return this._createWeekRange(date);
  }

  createPreview(activeDate: D | null): DateRange<D> {
    return this._createWeekRange(activeDate);
  }

  private _createWeekRange(date: D | null): DateRange<D> {
    if (date) {
      const day = this._dateAdapter.getDayOfWeek(date);
      const start = this._dateAdapter.addCalendarDays(date, 1 - day);
      const end = this._dateAdapter.addCalendarDays(date, 7 - day);
      return new DateRange<D>(start, end);
    }
    return new DateRange<D>(null, null);
  }
}

@Component({
  selector: 'xemex-time-selector',
  standalone: true,
  providers: [
    provideMomentDateAdapter(DATE_FORMATS),
    { provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: WeekRangeSelectionStrategy },
  ],
  imports: [MatDatepickerModule, MatIconModule, FormsModule, ReactiveFormsModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './time-selector.component.html',
  styleUrl: './time-selector.component.scss',
})
export class TimeSelectorComponent implements OnInit {
  period = input<ChartRangeOptions>(ChartRangeOptions.Day);
  isTopological = input<boolean>(false);
  isQuarterHour = input<boolean>(false);
  // Where the controls start instead of today, read once when they are created. Nothing is
  // emitted for it: the parent already holds that period's data.
  initialDate = input<Moment | null>(null);
  /** Draws the control as a white bordered box without dividers between its buttons. */
  framed = input<boolean>(false);

  change = output<string>();
  // The same moments as `change`, as the telemetry API's granularity and period. The week carries
  // the ISO week-year, which `change` does not.
  periodChange = output<TelemetryPeriod>();
  selectedDate = output<Moment | null>();

  readonly ChartRangeOptions = ChartRangeOptions;

  // Used by day / month / year modes
  readonly date = new FormControl<Moment>(moment());

  // Used by week mode (range picker needs start + end)
  readonly weekDateStart = new FormControl<Moment>(moment().isoWeekday(1));
  readonly weekDateEnd = new FormControl<Moment>(moment().isoWeekday(7));

  readonly maxDate = new Date(moment().year(), moment().month(), moment().date());

  // Tracks the period the controls currently hold, so a period switch can carry the
  // reference date over instead of resetting it. null until the first effect run.
  private previousPeriod: ChartRangeOptions | null = null;

  constructor() {
    effect(() => {
      const period = this.period();
      // Skip the initial run: the parent already loads data for the initial range,
      // and emitting here would trigger a redundant fetch.
      if (this.previousPeriod === null) {
        this.previousPeriod = period;
        return;
      }
      if (period === this.previousPeriod) return;
      this.onPeriodChanged(this.previousPeriod, period);
      this.previousPeriod = period;
    });
  }

  ngOnInit(): void {
    const initial = this.initialDate();
    if (initial) {
      this.date.setValue(initial.clone());
      this.weekDateStart.setValue(initial.clone().startOf('isoWeek'));
      this.weekDateEnd.setValue(initial.clone().endOf('isoWeek'));
    }
  }

  get displayDate(): string {
    switch (this.period()) {
      case ChartRangeOptions.Day:
      case ChartRangeOptions.DayPerQuarter:
        return (this.date.value ?? moment()).format('DD MMMM YYYY');
      case ChartRangeOptions.Week: {
        const start = this.weekDateStart.value ?? moment().isoWeekday(1);
        const end = this.weekDateEnd.value ?? moment().isoWeekday(7);
        return start.month() !== end.month()
          ? `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`
          : `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
      }
      case ChartRangeOptions.Month:
        return (this.date.value ?? moment()).format('MMMM YYYY');
      case ChartRangeOptions.Year:
        return (this.date.value ?? moment()).format('YYYY');
      default:
        return (this.date.value ?? moment()).format('DD MMMM YYYY');
    }
  }

  get datepickerStartView(): 'month' | 'multi-year' {
    const p = this.period();
    return p === ChartRangeOptions.Month || p === ChartRangeOptions.Year ? 'multi-year' : 'month';
  }

  get datepickerPanelClass(): string {
    if (this.period() === ChartRangeOptions.Month) return 'month-picker';
    if (this.period() === ChartRangeOptions.Year) return 'year-picker';
    return '';
  }

  isNextDisabled(): boolean {
    switch (this.period()) {
      case ChartRangeOptions.Day:
      case ChartRangeOptions.DayPerQuarter:
        return (this.date.value ?? moment()).isSameOrAfter(moment(), 'day');
      case ChartRangeOptions.Week:
        return (this.weekDateStart.value ?? moment()).isSameOrAfter(moment().isoWeekday(1), 'day');
      case ChartRangeOptions.Month:
        return (this.date.value ?? moment()).isSameOrAfter(moment(), 'month');
      case ChartRangeOptions.Year:
        return (this.date.value ?? moment()).isSameOrAfter(moment(), 'year');
    }
  }

  prev(): void {
    if (this.period() === ChartRangeOptions.Week) {
      this.shiftWeek(-7);
    } else {
      this.date.setValue(this.step(-1));
      this.emitChange();
    }
  }

  next(): void {
    if (this.period() === ChartRangeOptions.Week) {
      this.shiftWeek(7);
    } else {
      this.date.setValue(this.step(1));
      this.emitChange();
    }
  }

  onDateChanged(): void {
    this.emitChange();
  }

  onWeekStartChanged(_event: MatDatepickerInputEvent<Moment, DateRange<Moment>>): void {
    const start = this.weekDateStart.value ?? moment().isoWeekday(1);
    this.change.emit(`week-per-day/${start.year()}/${start.isoWeek()}`);
    this.periodChange.emit(telemetryPeriod(ChartRangeOptions.Week, start));
  }

  onMonthSelected(normalized: Moment, dp: MatDatepicker<Moment>): void {
    if (this.period() !== ChartRangeOptions.Month) return;
    const val = (this.date.value ?? moment()).clone();
    val.month(normalized.month()).year(normalized.year());
    this.date.setValue(val);
    dp.close();
    this.emitChange();
  }

  onYearSelected(normalized: Moment, dp: MatDatepicker<Moment>): void {
    if (this.period() !== ChartRangeOptions.Year) return;
    const val = (this.date.value ?? moment()).clone();
    val.year(normalized.year());
    this.date.setValue(val);
    dp.close();
    this.emitChange();
  }

  private shiftWeek(days: number): void {
    const newStart = (this.weekDateStart.value ?? moment().isoWeekday(1)).clone().add(days, 'days');
    const newEnd = newStart.clone().add(6, 'days');
    this.weekDateStart.setValue(newStart);
    this.weekDateEnd.setValue(newEnd);
    this.change.emit(`week-per-day/${newStart.year()}/${newStart.isoWeek()}`);
    this.periodChange.emit(telemetryPeriod(ChartRangeOptions.Week, newStart));
  }

  private step(direction: 1 | -1): Moment {
    const val = this.date.value ?? moment();
    switch (this.period()) {
      case ChartRangeOptions.Day:
      case ChartRangeOptions.DayPerQuarter:
        return val.clone().add(direction, 'day');
      case ChartRangeOptions.Month:
        return val.clone().add(direction, 'month');
      case ChartRangeOptions.Year:
        return val.clone().add(direction, 'year');
      default:
        return val.clone();
    }
  }

  // When the period switches, carry the currently referenced date over to the new
  // period's controls and emit it re-encoded, so the shown date and the data the
  // parent fetches stay in sync (e.g. Month → Day keeps today rather than snapping
  // the data to the 1st of the month while the picker still shows today).
  private onPeriodChanged(from: ChartRangeOptions, to: ChartRangeOptions): void {
    const ref = this.referenceFor(from);
    if (to === ChartRangeOptions.Week) {
      const start = ref.clone().startOf('isoWeek');
      this.weekDateStart.setValue(start);
      this.weekDateEnd.setValue(ref.clone().endOf('isoWeek'));
      this.change.emit(`week-per-day/${start.year()}/${start.isoWeek()}`);
      this.periodChange.emit(telemetryPeriod(ChartRangeOptions.Week, start));
    } else {
      this.date.setValue(ref);
      this.emitChange();
    }
  }

  // The date the controls currently point at, read from whichever control drives the
  // given period (week mode uses its own range controls).
  private referenceFor(period: ChartRangeOptions): Moment {
    if (period === ChartRangeOptions.Week) {
      return (this.weekDateStart.value ?? moment().isoWeekday(1)).clone();
    }
    return (this.date.value ?? moment()).clone();
  }

  private emitChange(): void {
    const val = this.date.value;
    if (!val) return;
    switch (this.period()) {
      case ChartRangeOptions.Day:
      case ChartRangeOptions.DayPerQuarter: {
        const date = val.format('YYYY-MM-DD');
        this.selectedDate.emit(val);
        if (this.isTopological()) {
          this.change.emit(
            this.isQuarterHour() ? `day-per-quarter-hour/${date}` : `day-per-hour/${date}`,
          );
        } else {
          this.change.emit(`day-per-ten-minutes/${date}`);
        }
        break;
      }
      case ChartRangeOptions.Month:
        this.change.emit(`month-per-day/${val.year()}/${val.month() + 1}`);
        break;
      case ChartRangeOptions.Year:
        this.change.emit(`year-per-month/${val.year()}`);
        break;
    }
    if (this.period() !== ChartRangeOptions.Week) {
      this.periodChange.emit(telemetryPeriod(this.period(), val));
    }
  }
}
